import { Temporal } from '@js-temporal/polyfill'

import { isTemplateCampaignActive } from '../helpers/isTemplateCampaignActive'
import { isTemplateStartDateInFuture } from '../helpers/isTemplateStartDateInFuture'
import { withNewTagIfRecent } from '../helpers/withNewTagIfRecent'
import type { Template, TemplateCampaign } from '../types'
import {
  isDripCampaign,
  isTemplateInCampaign,
  isTemplateVisibleInCampaign,
} from './utils'

/**
 * Computes the current day number for a campaign based on calendar days since start.
 */
export const computeCampaignDayNumber = (
  campaign: TemplateCampaign,
  currentTime: Temporal.ZonedDateTime,
): number => {
  const startPlainDate = campaign.startDate.toPlainDate()
  const currentPlainDate = currentTime.toPlainDate()

  // If before campaign start, return 1
  if (Temporal.PlainDate.compare(currentPlainDate, startPlainDate) < 0) {
    return 1
  }

  const daysSinceStart =
    currentPlainDate.since(startPlainDate, { largestUnit: 'days' }).days + 1

  // Cap at the number of templates
  const maxDay = campaign.templateIds?.length ?? daysSinceStart
  return Math.min(daysSinceStart, maxDay)
}

/**
 * Filters campaigns to only those that are active at the given time.
 * Also computes and adds currentDayNumber to each campaign.
 */
export const filterActiveCampaigns = (
  campaigns: TemplateCampaign[],
  currentTime: Temporal.ZonedDateTime,
): TemplateCampaign[] => {
  return campaigns
    .filter((campaign) => {
      // Only consider Active status campaigns
      if (campaign.status !== 'Active') {
        return false
      }
      // Check if current time is within campaign date range
      return (
        Temporal.ZonedDateTime.compare(currentTime, campaign.startDate) >= 0 &&
        Temporal.ZonedDateTime.compare(currentTime, campaign.endDate) <= 0
      )
    })
    .map((campaign) =>
      Object.assign({}, campaign, {
        currentDayNumber: computeCampaignDayNumber(campaign, currentTime),
      }),
    )
    .toSorted((a, b) => {
      // Priority 1: Drip Campaign type takes precedence
      const aIsDrip = a.campaignType === 'Drip Campaign'
      const bIsDrip = b.campaignType === 'Drip Campaign'
      if (aIsDrip && !bIsDrip) return -1
      if (!aIsDrip && bIsDrip) return 1

      // Priority 2: Most recent startDate wins (descending)
      return Temporal.ZonedDateTime.compare(b.startDate, a.startDate)
    })
}

/**
 * Orders templates by campaign priority using a Set to maintain insertion order:
 * 1. Templates from active campaigns first (in campaign priority order, using their templateIds)
 * 2. Remaining templates after
 *
 * The Set approach ensures campaign templates appear first without duplicates,
 * and preserves the order templates were added.
 */
export const orderTemplatesByCampaignPriority = (
  templates: Template[],
  activeCampaigns: TemplateCampaign[],
): Template[] => {
  // Build ordered Set: campaign templates first, then the rest
  const orderedIds = new Set<string>()

  // Add template IDs from active campaigns (campaigns are already sorted by priority)
  for (const campaign of activeCampaigns) {
    if (campaign.templateIds) {
      for (const id of campaign.templateIds) {
        orderedIds.add(id)
      }
    }
  }

  // Add remaining template IDs (Set ignores duplicates)
  for (const template of templates) {
    orderedIds.add(template.id)
  }

  // Create a map for O(1) lookup
  const templateMap = new Map(templates.map((t) => [t.id, t]))

  // Return templates in Set order, filtering out any IDs that weren't in input templates
  return Array.from(orderedIds)
    .map((id) => templateMap.get(id))
    .filter((t): t is Template => t !== undefined)
}

/**
 * Filters templates based on all time-related rules and adds computed tags.
 * Returns templates that pass all visibility checks with 'New' tag added where applicable.
 * Templates are ordered by campaign priority (campaign templates first).
 */
export const filterVisibleTemplates = (
  templates: Template[],
  campaigns: TemplateCampaign[],
  activeCampaigns: TemplateCampaign[],
  currentTime: Temporal.ZonedDateTime,
): Template[] => {
  const filtered = templates
    .filter((template) => {
      // Exception for Creator Camp "Growth": return templates if linked campaign internal name contains `Creator Camp Growth`
      const isCreatorCampGrowthTemplate = campaigns.some(
        (campaign) =>
          campaign.name?.includes('Creator Camp Growth') &&
          campaign.templateIds?.includes(template.id),
      )
      if (isCreatorCampGrowthTemplate) {
        return true
      }

      // 1. Hide templates whose campaign hasn't started yet
      if (isTemplateStartDateInFuture(template, campaigns, currentTime)) {
        return false
      }

      // 2. Check if template's campaign is visible
      if (!isTemplateCampaignActive(template, currentTime, campaigns)) {
        return false
      }

      // 3. Check drip campaign day visibility
      const templateDripCampaigns = activeCampaigns.filter(
        (campaign) =>
          isDripCampaign(campaign) && isTemplateInCampaign(template, campaign),
      )

      if (templateDripCampaigns.length > 0) {
        // Must be visible in at least one drip campaign
        const visibleInAnyDripCampaign = templateDripCampaigns.some(
          (campaign) =>
            isTemplateVisibleInCampaign(template, {
              ...campaign,
              currentDayNumber: computeCampaignDayNumber(campaign, currentTime),
            }),
        )
        if (!visibleInAnyDripCampaign) {
          return false
        }
      }

      return true
    })
    .map((template) => withNewTagIfRecent(template, currentTime))

  // Order templates: campaign templates first, then the rest
  return orderTemplatesByCampaignPriority(filtered, activeCampaigns)
}
